import React from 'react';
import styled from 'styled-components';
import type { CanvasPage } from './DocumentCanvasPanel';
import type { AdjacentDocumentContext, AdjacentPageItem } from '../../types';
import { selectPageImageWidth, withPageImageWidth } from '../../utils/pageImages';
import { pickMostVisible, slotHeight } from '../../utils/continuousScroll';

/**
 * Preview/Acrobat-style continuous page scroller.
 *
 * Every page gets a slot with its exact reserved height (from the dims
 * recorded at ingest), so the scrollbar is honest and scroll-to-page is
 * precise. Images mount only near the viewport (one IntersectionObserver with
 * a generous rootMargin acts as both windowing and prefetch); a second
 * observer tracks per-page visibility to report the "current" page upward.
 * Mounted images load the width bucket matching zoom × devicePixelRatio, with
 * the 160w rail thumb (usually already in the browser cache) as a blurred
 * placeholder until the full image decodes.
 */
export interface ContinuousPageStackProps {
    /** Pages in display order (already filtered to match the rail). */
    pages: CanvasPage[];
    zoom: number;
    /** Base CSS width of a page at zoom 1 (DocumentCanvasPanel's constant). */
    baseWidth: number;
    /** Navigation target: the stack scrolls to this page when `scrollToken`
     *  changes (token increments on every explicit nav, so re-clicking the
     *  same page still snaps to it). */
    activePageId: string | null;
    scrollToken: number;
    /** Identity of the document being shown. The scroll container is the same
     *  DOM node across document switches, so when this changes the stack snaps
     *  back to the top of the new document (its first page). */
    scrollResetKey?: string | number | null;
    /** Viewport-derived current page (drives rail highlight / meta / OCR). */
    onCurrentPageChange?: (pageId: string) => void;
    /** Adjacent-document context (moved out of the page rail): a divider above
     *  the first / below the last page with a show/hide toggle for a few
     *  sample pages of the neighboring document. */
    previousDocument?: AdjacentDocumentContext | null;
    nextDocument?: AdjacentDocumentContext | null;
    onNavigateToDocument?: (docId: string) => void;
}

// How far beyond the viewport images stay mounted. Acts as the prefetch
// look-ahead AND the unmount hysteresis; ~1.5 viewports each way keeps flings
// smooth without holding hundreds of decoded pages in the DOM.
const MOUNT_MARGIN = '150% 0px';

const Stack = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    overflow: auto;
`;

const Inner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding: 4px 0 24px;
    /* Wider than the pane -> the stack scrolls horizontally as one unit. */
    min-width: fit-content;
`;

const Slot = styled.div<{ $width: number; $height: number }>`
    position: relative;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height}px;
    background: #ffffff;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
`;

const SlotImg = styled.img<{ $ready: boolean }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: ${({ $ready }) => ($ready ? 1 : 0)};
    transition: opacity 0.15s ease;
`;

const SlotThumb = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: blur(2px);
`;

const PageCaption = styled.div`
    font-size: 11px;
    color: #94a3b8;
    text-align: center;
    margin-top: -12px;
`;

/* ── Adjacent document context (moved out of the page rail) ── */

const AdjacentDividerRow = styled.div<{ $width: number }>`
    width: ${({ $width }) => $width}px;
    display: flex;
    align-items: center;
    gap: 10px;
    user-select: none;
`;

const AdjacentDividerLine = styled.div`
    flex: 1;
    height: 0;
    border-top: 1px dashed #cbd5e1;
`;

const AdjacentDocLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 60%;
    padding: 2px 6px;
    border: none;
    border-radius: 4px;
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    cursor: pointer;
    font-family: inherit;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &:hover {
        color: #475569;
        text-decoration: underline;
    }
`;

const AdjacentToggleButton = styled.button`
    flex-shrink: 0;
    padding: 2px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;

    &:hover {
        color: #475569;
        border-color: #cbd5e1;
    }
`;

const AdjacentSlot = styled.div<{ $width: number }>`
    width: ${({ $width }) => $width}px;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    opacity: 0.65;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.9;
    }

    img {
        display: block;
        width: 100%;
        height: auto;
    }
`;

const AdjacentEllipsis = styled.div`
    font-size: 12px;
    color: #cbd5e1;
    letter-spacing: 0.2em;
    user-select: none;
`;

/** Upgrade a rail-thumb URL to reading resolution (mirrors the workspace's
 *  activePage logic): identity URLs switch their width bucket; legacy URLs
 *  swap the /thumb/ endpoint. */
const adjacentImageUrl = (page: AdjacentPageItem, displayWidth: number): string | undefined => {
    const thumbUrl = page.thumbnailUrl;
    if (!thumbUrl) {
        return undefined;
    }
    return thumbUrl.includes('/img?')
        ? withPageImageWidth(thumbUrl, selectPageImageWidth(displayWidth))
        : thumbUrl.replace('/thumb/', '/full/');
};

/** Divider + optional sample pages for the previous/next document. The pages
 *  render smaller and dimmed so they read as context, not part of this doc. */
const AdjacentDocBlock: React.FC<{
    context: AdjacentDocumentContext;
    side: 'previous' | 'next';
    displayWidth: number;
    onNavigate?: (docId: string) => void;
}> = ({ context, side, displayWidth, onNavigate }) => {
    const [expanded, setExpanded] = React.useState(false);
    // A new adjacent document (doc switch) starts collapsed again.
    React.useEffect(() => setExpanded(false), [context.docId]);

    const title = context.title ?? context.docId;
    const sampleWidth = Math.round(displayWidth * 0.7);
    const hiddenCount = context.totalPages - context.pages.length;
    const divider = (
        <AdjacentDividerRow $width={displayWidth}>
            <AdjacentDividerLine />
            <AdjacentDocLink
                type="button"
                title={`Go to ${side} document: ${title}`}
                onClick={() => onNavigate?.(context.docId)}
            >
                <span>{side === 'previous' ? '↑' : '↓'} {title}</span>
            </AdjacentDocLink>
            {context.pages.length > 0 && (
                <AdjacentToggleButton
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded(e => !e)}
                >
                    {expanded ? 'Hide pages' : 'Show pages'}
                </AdjacentToggleButton>
            )}
            <AdjacentDividerLine />
        </AdjacentDividerRow>
    );
    const samples = expanded && (
        <>
            {side === 'previous' && hiddenCount > 0 && (
                <AdjacentEllipsis aria-label={`${hiddenCount} earlier pages not shown`}>···</AdjacentEllipsis>
            )}
            {context.pages.map(page => {
                const src = adjacentImageUrl(page, sampleWidth);
                return (
                    <React.Fragment key={page.pageId}>
                        <AdjacentSlot
                            $width={sampleWidth}
                            title={`Go to ${title}, page ${page.pageNumber}`}
                            onClick={() => onNavigate?.(context.docId)}
                        >
                            {src && <img src={src} alt={`${title} page ${page.pageNumber}`} loading="lazy" />}
                        </AdjacentSlot>
                        <PageCaption>{title} — Page {page.pageNumber}</PageCaption>
                    </React.Fragment>
                );
            })}
            {side === 'next' && hiddenCount > 0 && (
                <AdjacentEllipsis aria-label={`${hiddenCount} later pages not shown`}>···</AdjacentEllipsis>
            )}
        </>
    );
    // Previous doc: sample pages sit ABOVE the divider (they precede this doc);
    // next doc: below.
    return side === 'previous' ? <>{samples}{divider}</> : <>{divider}{samples}</>;
};

interface PageSlotProps {
    page: CanvasPage;
    width: number;
    height: number;
    mounted: boolean;
    imageWidth: ReturnType<typeof selectPageImageWidth>;
}

const PageSlot: React.FC<PageSlotProps> = ({ page, width, height, mounted, imageWidth }) => {
    const [ready, setReady] = React.useState(false);
    const src = page.imageUrl ? withPageImageWidth(page.imageUrl, imageWidth) : undefined;
    const thumbSrc = page.imageUrl ? withPageImageWidth(page.imageUrl, 160) : undefined;

    React.useEffect(() => {
        // Reset the fade when the bucket (zoom) changes; the old image stays
        // visible via the thumb layer until the new bucket decodes.
        setReady(false);
    }, [src]);

    return (
        <Slot $width={width} $height={height} data-page-id={page.id}>
            {mounted && thumbSrc && !ready && <SlotThumb src={thumbSrc} alt="" aria-hidden />}
            {mounted && src && (
                <SlotImg
                    src={src}
                    alt={`Document page ${page.pageNumber}`}
                    $ready={ready}
                    onLoad={() => setReady(true)}
                />
            )}
        </Slot>
    );
};

export const ContinuousPageStack: React.FC<ContinuousPageStackProps> = ({
    pages,
    zoom,
    baseWidth,
    activePageId,
    scrollToken,
    scrollResetKey,
    onCurrentPageChange,
    previousDocument,
    nextDocument,
    onNavigateToDocument,
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [mountedIds, setMountedIds] = React.useState<Set<string>>(new Set());
    // Visibility ratios + the derived current page live in refs so observer
    // callbacks don't re-render the whole stack on every scroll tick.
    const ratiosRef = React.useRef<Map<string, number>>(new Map());
    const currentIdRef = React.useRef<string | null>(null);
    const onCurrentPageChangeRef = React.useRef(onCurrentPageChange);
    onCurrentPageChangeRef.current = onCurrentPageChange;

    const displayWidth = Math.round(baseWidth * zoom);
    const imageWidth = selectPageImageWidth(displayWidth);

    // (Re)arm the observers whenever the page set changes. Slots are looked up
    // by data-page-id so the observers survive slot re-renders.
    React.useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof IntersectionObserver === 'undefined') {
            // No IO (jsdom/SSR): mount everything, skip current-page tracking.
            setMountedIds(new Set(pages.map(p => p.id)));
            return;
        }

        const mountObserver = new IntersectionObserver(
            entries => {
                setMountedIds(prev => {
                    const next = new Set(prev);
                    for (const entry of entries) {
                        const id = (entry.target as HTMLElement).dataset.pageId;
                        if (!id) continue;
                        if (entry.isIntersecting) next.add(id);
                        else next.delete(id);
                    }
                    return next;
                });
            },
            { root: container, rootMargin: MOUNT_MARGIN },
        );

        const currentObserver = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    const id = (entry.target as HTMLElement).dataset.pageId;
                    if (!id) continue;
                    ratiosRef.current.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
                }
                const next = pickMostVisible(ratiosRef.current, currentIdRef.current);
                if (next && next !== currentIdRef.current) {
                    currentIdRef.current = next;
                    onCurrentPageChangeRef.current?.(next);
                }
            },
            { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] },
        );

        ratiosRef.current = new Map();
        const slots = container.querySelectorAll('[data-page-id]');
        slots.forEach(slot => {
            mountObserver.observe(slot);
            currentObserver.observe(slot);
        });
        return () => {
            mountObserver.disconnect();
            currentObserver.disconnect();
        };
    }, [pages]);

    // Explicit navigation: snap to the requested page. Token-driven so
    // re-clicking the rail entry for the page you're already "on" re-aligns it.
    React.useEffect(() => {
        if (scrollToken === 0 || !activePageId) {
            return;
        }
        const container = containerRef.current;
        const slot = container?.querySelector(`[data-page-id="${CSS.escape(activePageId)}"]`);
        slot?.scrollIntoView({ block: 'start' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollToken]);

    // Document switch: snap back to the top of the NEW document. Targets the
    // first page rather than scrollTop=0 so a previous-document divider above
    // page 1 doesn't become the landing view. Skipped on initial mount (the
    // container starts at the top anyway, and scrollIntoView could disturb
    // ancestor scrollers). Also forgets the old doc's "current" page so the
    // first viewport report for the new document fires cleanly.
    const skipInitialResetRef = React.useRef(true);
    React.useEffect(() => {
        if (skipInitialResetRef.current) {
            skipInitialResetRef.current = false;
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }
        currentIdRef.current = null;
        const firstId = pages[0]?.id;
        const slot = firstId
            ? container.querySelector(`[data-page-id="${CSS.escape(firstId)}"]`)
            : null;
        if (slot) {
            slot.scrollIntoView({ block: 'start' });
        } else {
            container.scrollTop = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollResetKey]);

    return (
        <Stack ref={containerRef}>
            <Inner>
                {previousDocument && (
                    <AdjacentDocBlock
                        context={previousDocument}
                        side="previous"
                        displayWidth={displayWidth}
                        onNavigate={onNavigateToDocument}
                    />
                )}
                {pages.map(page => {
                    const height = slotHeight(page, displayWidth);
                    return (
                        <React.Fragment key={page.id}>
                            <PageSlot
                                page={page}
                                width={displayWidth}
                                height={height}
                                mounted={mountedIds.has(page.id)}
                                imageWidth={imageWidth}
                            />
                            <PageCaption>Page {page.pageNumber}</PageCaption>
                        </React.Fragment>
                    );
                })}
                {nextDocument && (
                    <AdjacentDocBlock
                        context={nextDocument}
                        side="next"
                        displayWidth={displayWidth}
                        onNavigate={onNavigateToDocument}
                    />
                )}
            </Inner>
        </Stack>
    );
};
